/* eslint-disable react/jsx-curly-brace-presence */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import {
  apiCredentials,
  modals
} from 'ducks';
import Placeholder from 'components/Placeholder';
import { stringParams } from 'utils';
import FA from 'utils/FA';
import { duckify, filterDuck } from 'utils/duckify';
import { Button } from 'react-bootstrap';
import ModalComponent from '../ModalComponent';

import './styles/ApiCredentialsModal.scss';

const ApiCredentialsModal = (props) => {
  const {
    credentials,
    generateJwt,
    displayJwt,
    regenerateSecret,
    displaySecret,
    onClose
  } = props;

  const [revealSecret, setRevealSecret] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [externalProcessing, setExternalProcessing] = useState(false);
  const focusRef = useRef();

  const completedProcessing = () => {
    setProcessing(false);
    setExternalProcessing(false);
  };

  if (externalProcessing) {
    // Suppress this modal while we're waiting on an external action to prevent double modal display
    return null;
  }

  return (
    <ModalComponent title={'API Credential Details'} onClose={onClose}>
      <Placeholder.text
        ready={credentials.ready}
        width={500}
        content={() => {
          if (!credentials.ready) {
            return null;
          }

          return credentials.map(credential => (
            <div className={'api-credential-detail'} key={credential.id}>
              <div className={'detail-item'}>
                <span className={'detail-item__label'}>Credential ID</span>
                <div className={'detail-item__detail'}>
                  <code className={'form-control-plaintext'}>{credential.id}</code>
                </div>
              </div>
              {/* Generate JWT */}
              <div className={'detail-item'}>
                <span className={'detail-item__label'}>Generate JWT</span>
                <div className={'detail-item__detail'}>
                  <Button
                    id={`${credential.id}__jwt`}
                    variant={'primary'}
                    size={'sm'}
                    disabled={processing}
                    onClick={() => {
                      setProcessing(true);
                      generateJwt(credential)
                        .then(generatedJwt => {
                          setExternalProcessing(true);
                          displayJwt(generatedJwt)
                            .then(completedProcessing)
                            .catch(completedProcessing);
                        })
                        .catch(completedProcessing);
                    }}
                  >
                    Generate JWT
                  </Button>
                  <div className={'form-text'}>
                    To generate a short-lived JWT that can be used for making API calls,
                    click &quot;Generate JWT&quot;.
                  </div>
                </div>
              </div>
              {/* Shared Secret */}
              {credential.shared_secret && (
                <div className={'detail-item'}>
                  <label className={'detail-item__label'} htmlFor={`${credential.id}__secret`}>API Secret</label>
                  <div className={'detail-item__detail'}>
                    <div className={'input-group'}>
                      <input
                        ref={focusRef}
                        className={'form-control form-control-sm'}
                        id={`${credential.id}__secret`}
                        name={`${credential.id}__secret`}
                        type={revealSecret ? 'text' : 'password'}
                        defaultValue={credential.shared_secret}
                      />
                      <Button
                        variant={'secondary'}
                        onClick={() => {
                          setRevealSecret(!revealSecret);
                          if (!revealSecret) { // if current value is false we just toggled to reveal
                            focusRef.current.focus();
                            focusRef.current.select();
                          }
                        }}
                      >
                        {revealSecret ? 'Hide Secret' : 'Reveal Secret'}
                      </Button>
                    </div>
                    <div className={'form-text'}>
                      Click to reveal the API Secret for this credential.
                      The secret can be used to generate JWTs for making API requests.
                    </div>
                  </div>
                </div>
              )}
              {/* Regenerate Secret */}
              <div className={'detail-item'}>
                <span className={'detail-item__label'}>Regenerate Secret</span>
                <div className={'detail-item__detail'}>
                  <Button
                    variant={'outline-danger'}
                    disabled={processing}
                    onClick={() => {
                      setExternalProcessing(true);
                      regenerateSecret(credential)
                        .then(displaySecret, completedProcessing)
                        .then(completedProcessing)
                        .catch(completedProcessing);
                    }}
                  >
                    Regenerate Secret
                  </Button>
                  <div className={'form-text'}>
                    If you need to regenerate the API Secret for this credential, click &quot;Regenerate Secret&quot;.
                  </div>
                </div>
              </div>
            </div>
          ));
        }}
      />
    </ModalComponent>
  );
};

ApiCredentialsModal.propTypes = {
  apiUser: PropTypes.shape({ id: PropTypes.string }).isRequired,
  credentials: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    api_user_id: PropTypes.string,
    created_at: PropTypes.string,
    shared_secret: PropTypes.string
  })).isRequired,
  generateJwt: PropTypes.func.isRequired,
  displayJwt: PropTypes.func.isRequired,
  regenerateSecret: PropTypes.func.isRequired,
  displaySecret: PropTypes.func.isRequired,
  onClose: PropTypes.func
};

ApiCredentialsModal.defaultProps = {
  onClose: () => false
};

const GeneratedJwtMessage = ({ jwt, ttl }) => (
  <div>
    <p>
      You can find your JSON Web Token below. This token can be used to make API requests until it expires
      {' '}
      <strong>{moment.duration(ttl, 'seconds').humanize(true)}</strong>
      .
    </p>
    <p>
      To use the token, pass it as a Bearer token in the Authorization header when making an API request.
      For example:
      &nbsp;
      <code>Authorization: Bearer &lt;token&gt;</code>
    </p>
    <textarea
      className={'form-control form-control-sm'}
      rows={4}
      value={jwt}
      readOnly
    />
  </div>
);

GeneratedJwtMessage.propTypes = {
  jwt: PropTypes.string.isRequired,
  ttl: PropTypes.number.isRequired
};

const ConfirmRegenerateMessage = () => (
  <div>
    <h2>Are you sure you want to regenerate this API Secret?</h2>
    <p>
      Regenerating your API secret is non-reversible. Any applications using the current secret
      will not be able to create valid JWTs until updated with the new secret. Any JWTs issued
      using the previous secret will be immediately invalidated.
    </p>
  </div>
);

// eslint-disable-next-line camelcase
const DisplayApiSecretMessage = ({ id, shared_secret }) => (
  <div>
    <p>
      This API Secret can be used to create JSON Web Tokens (JWTs) for making API requests.
    </p>
    <div className={'alert alert-warning'}>
      <FA icon={faExclamationTriangle} postNbsp />
      Please record this API Secret before dismissing this dialog.
    </div>
    <dl>
      <dt>Your Credential ID is:</dt>
      <dd>
        <code>{id}</code>
      </dd>
      <dt>Your API Secret is:</dt>
      <dd>
        {/* eslint-disable-next-line camelcase */}
        <code>{shared_secret}</code>
      </dd>
    </dl>
  </div>
);

DisplayApiSecretMessage.propTypes = {
  shared_secret: PropTypes.string.isRequired
};

export { DisplayApiSecretMessage };

const mapDucksToProps = ({ apiUser }) => ({
  credentials: filterDuck(apiCredentials, { api_user_id: apiUser.id })
});

const mapDispatchToProps = (dispatch) => ({
  generateJwt: credential => dispatch(apiCredentials.actions.generateJwt(credential.id)),
  displayJwt: generatedJwt => new Promise((resolve) => {
    dispatch(modals.actions.show('MessageModal', {
      title: 'Generated JWT',
      message: <GeneratedJwtMessage {...generatedJwt} />,
      onClose: () => {
        dispatch(modals.actions.hide());
        resolve();
      }
    }));
  }),
  regenerateSecret: credential => new Promise((resolve, reject) => {
    dispatch(modals.actions.show('VerificationModal', {
      title: 'Confirm Regenerate Secret',
      message: <ConfirmRegenerateMessage />,
      onClose: () => {
        dispatch(modals.actions.hide());
        reject();
      },
      onConfirm: () => dispatch(apiCredentials.actions.regenerateSecret(credential.id))
        .then(apiCredentials.actions.denormalizeObjects)
        .then(regenerated => {
          dispatch(apiCredentials.actions.markDirty(stringParams({ api_user_id: credential.api_user_id })));
          resolve(regenerated);
        })
        .catch(() => {
          dispatch(modals.actions.show('UnexpectedError', {
            onClose: () => {
              dispatch(modals.actions.hide_all());
              reject();
            }
          }));
        })
    }));
  }),
  displaySecret: apiCredential => new Promise((resolve) => dispatch(modals.actions.show('MessageModal', {
    title: 'API Secret',
    message: <DisplayApiSecretMessage {...apiCredential} />,
    onClose: () => {
      dispatch(modals.actions.hide());
      resolve();
    }
  })))
});

export default duckify(mapDucksToProps, () => ({}), mapDispatchToProps)(ApiCredentialsModal);
